<template>
  <div class="sale-scanner">
    <div class="scanner-header">
      <h2>📱 Sale Scanner</h2>
      <p>Welcome, {{ authStore.user?.email }}! Scan barcodes or search by SKU</p>
    </div>

    <div class="scanner-container">
      <!-- Search Section -->
      <div class="search-section">
        <div class="search-header">
          <h3>🔍 Product Search</h3>
          <p>Search by SKU, barcode, or product name</p>
        </div>
        
        <ProductSearch
          :show-filters="false"
          :show-recent-searches="true"
          :max-results="5"
          search-type="all"
          placeholder="Search by SKU, barcode, or product name..."
          @select="onProductSelect"
          @search="onSearch"
        />
      </div>

      <!-- Camera Section -->
      <div class="camera-section">
        <div class="camera-header">
          <h3>📷 Barcode Scanner</h3>
          <div class="scanner-status" :class="{ 'active': isScanning }">
            {{ isScanning ? 'Camera Active' : 'Camera Inactive' }}
          </div>
        </div>

        <div v-if="!isScanning" class="camera-controls">
          <button @click="startScanning" class="btn btn-primary btn-large">
            <span class="icon">📷</span>
            Start Scanner
          </button>
          <p class="camera-info">Point camera at product barcode to scan</p>
        </div>

        <div v-else class="camera-viewport">
          <qrcode-stream
            :paused="!isScanning"
            :formats="['qr_code', 'code_128', 'code_39', 'code_93', 'ean_13', 'ean_8']"
            @detect="onDetect"
            @error="onError"
            @camera-on="onCameraInit"
            class="qr-reader"
          />
          
          <div class="scanner-overlay">
            <div class="scan-area">
              <div class="scan-line"></div>
            </div>
          </div>
          
          <div class="camera-controls">
            <button @click="stopScanning" class="btn btn-secondary">
              <span class="icon">⏹️</span>
              Stop Scanner
            </button>
          </div>
        </div>

        <div v-if="error" class="error-message">
          <p><strong>Error:</strong> {{ error }}</p>
          <button @click="clearError" class="btn btn-small">Clear Error</button>
        </div>
      </div>
    </div>

    <!-- Product Information Section -->
    <div class="product-section">
      <div class="product-header">
        <h3>📦 Product Information</h3>
        <div v-if="lastScannedCode" class="last-scan">
          Last scan: {{ lastScannedCode }} ({{ lastScanType }})
        </div>
      </div>

      <div v-if="loadingProduct" class="loading">
        <div class="spinner"></div>
        <p>Looking up product...</p>
      </div>

      <div v-else-if="currentProduct" class="product-info">
        <div class="product-details">
          <h4>{{ currentProduct.name }}</h4>
          <p v-if="currentProduct.description" class="product-description">
            {{ currentProduct.description }}
          </p>
          <div class="product-meta">
            <span class="sku">SKU: {{ currentProduct.sku }}</span>
            <span v-if="currentProduct.barcode" class="barcode">
              Barcode: {{ currentProduct.barcode }}
            </span>
            <span v-if="currentProduct.globalProduct" class="global-product">
              <Icon :name="currentProduct.globalProduct.status === 'VALID' ? '✅' : '⚠️'" />
              {{ currentProduct.globalProduct.status }}
            </span>
          </div>
          <div class="product-price">
            <span class="price">${{ currentProduct.price?.toFixed(2) || '0.00' }}</span>
            <span class="stock">Stock: {{ currentProduct.stockQuantity || 0 }}</span>
          </div>
        </div>

        <div class="quantity-controls">
          <label for="quantity">Quantity:</label>
          <div class="quantity-input">
            <button @click="decreaseQuantity" class="btn btn-small">-</button>
            <input 
              id="quantity"
              v-model="quantity" 
              type="number" 
              min="1" 
              :max="currentProduct.stockQuantity || 999"
              class="quantity-field"
            />
            <button @click="increaseQuantity" class="btn btn-small">+</button>
          </div>
        </div>

        <div class="sale-total">
          <span class="total-label">Total:</span>
          <span class="total-amount">${{ saleTotal.toFixed(2) }}</span>
        </div>

        <div class="sale-actions">
          <button 
            @click="processSale" 
            :disabled="processingSale || quantity < 1 || (currentProduct.stockQuantity && quantity > currentProduct.stockQuantity)"
            class="btn btn-success btn-large"
          >
            <span v-if="processingSale" class="spinner-small"></span>
            <span v-else class="icon">💰</span>
            {{ processingSale ? 'Processing...' : 'Process Sale' }}
          </button>
          <button @click="clearProduct" class="btn btn-secondary">
            <span class="icon">🗑️</span>
            Clear
          </button>
        </div>
      </div>

      <div v-else class="no-product">
        <div class="placeholder-icon">📦</div>
        <p>Search for a product or scan a barcode to begin</p>
        <div class="quick-actions">
          <button @click="focusSearch" class="btn btn-outline">
            🔍 Search Products
          </button>
          <button @click="startScanning" class="btn btn-outline">
            📷 Start Scanner
          </button>
        </div>
      </div>
    </div>

    <!-- Recent Sales -->
    <div v-if="recentSales.length > 0" class="recent-sales">
      <h3>📈 Recent Sales</h3>
      <div class="sales-list">
        <div 
          v-for="sale in recentSales.slice(0, 5)" 
          :key="sale.id"
          class="sale-item"
        >
          <div class="sale-info">
            <span class="product-name">{{ sale.productName }}</span>
            <span class="sale-details">
              Qty: {{ sale.quantitySold }} × ${{ sale.priceAtSale.toFixed(2) }}
            </span>
            <span class="sale-identifier">
              {{ sale.sku ? `SKU: ${sale.sku}` : `Barcode: ${sale.barcode}` }}
            </span>
          </div>
          <div class="sale-total">
            ${{ (sale.quantitySold * sale.priceAtSale).toFixed(2) }}
          </div>
          <div class="sale-time">
            {{ formatTime(sale.createdAt) }}
          </div>
        </div>
      </div>
    </div>

    <!-- Success/Error Toast -->
    <div v-if="notification" class="notification" :class="notification.type">
      <span class="notification-icon">
        {{ notification.type === 'success' ? '✅' : '❌' }}
      </span>
      <span class="notification-message">{{ notification.message }}</span>
      <button @click="clearNotification" class="notification-close">×</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { QrcodeStream } from 'vue-qrcode-reader'
import { useAuthStore } from '../stores/auth'
import ProductSearch from '../components/ProductSearch.vue'
import Icon from '../components/Icon.vue'
import api from '../services/api'

const authStore = useAuthStore()

// Scanner state
const isScanning = ref(false)
const error = ref(null)
const lastScannedCode = ref(null)
const lastScanType = ref(null)

// Product state
const currentProduct = ref(null)
const loadingProduct = ref(false)
const quantity = ref(1)

// Sale state
const processingSale = ref(false)
const recentSales = ref([])
const notification = ref(null)

// Computed properties
const saleTotal = computed(() => {
  return currentProduct.value && currentProduct.value.price 
    ? currentProduct.value.price * quantity.value 
    : 0
})

// Product search methods
const onProductSelect = (product) => {
  console.log('🔍 Product selected from search:', product)
  currentProduct.value = product
  quantity.value = 1
  lastScannedCode.value = product.sku
  lastScanType.value = 'SKU'
  
  // Stop scanning if active
  if (isScanning.value) {
    stopScanning()
  }
  
  showNotification(`Product selected: ${product.name}`, 'success')
}

const onSearch = (searchData) => {
  console.log('🔍 Search performed:', searchData)
}

const focusSearch = () => {
  // Focus on the search input
  const searchInput = document.querySelector('.product-search input')
  if (searchInput) {
    searchInput.focus()
  }
}

// Scanner methods
const startScanning = () => {
  isScanning.value = true
  error.value = null
}

const stopScanning = () => {
  isScanning.value = false
}

const onDetect = async (detectedCodes) => {
  console.log('🔍 Barcode detection triggered!', detectedCodes)
  
  if (detectedCodes.length > 0) {
    const barcode = detectedCodes[0].rawValue
    console.log('📊 Detected barcode:', barcode)
    console.log('📊 Barcode format:', detectedCodes[0].format)
    
    lastScannedCode.value = barcode
    lastScanType.value = 'Barcode'
    
    // Stop scanning temporarily to prevent multiple scans
    isScanning.value = false
    
    showNotification(`Detected: ${barcode}`, 'info')
    
    await lookupProduct(barcode, 'barcode')
    
    // Resume scanning after a brief delay if no product found
    setTimeout(() => {
      if (!currentProduct.value) {
        isScanning.value = true
      }
    }, 2000)
  } else {
    console.log('⚠️ Detection triggered but no codes found')
  }
}

const onError = (err) => {
  console.error('Scanner error:', err)
  error.value = err.message || 'Camera error occurred'
}

const onCameraInit = () => {
  error.value = null
}

const clearError = () => {
  error.value = null
}

// Product methods
const lookupProduct = async (identifier, type = 'barcode') => {
  console.log(`🔎 Looking up product by ${type}: ${identifier}`)
  loadingProduct.value = true
  
  try {
    // Use the new unified search API
    const response = await api.get('/search/products', {
      params: {
        q: identifier,
        type: type,
        limit: 1
      }
    })
    
    console.log(`✅ Search API response:`, response.data)
    
    if (response.data.products && response.data.products.length > 0) {
      currentProduct.value = response.data.products[0]
      quantity.value = 1
      
      showNotification(`Product found: ${response.data.products[0].name}`, 'success')
    } else {
      currentProduct.value = null
      
      showNotification(`Product not found for ${type}: ${identifier}`, 'error')
    }
  } catch (err) {
    console.error('❌ Product lookup error:', err)
    console.error('❌ Error details:', err.response)
    currentProduct.value = null
    
    const errorMessage = err.response?.data?.error || 'Failed to search for product'
    showNotification(`Search Error: ${errorMessage}`, 'error')
  } finally {
    loadingProduct.value = false
  }
}

const clearProduct = () => {
  currentProduct.value = null
  quantity.value = 1
  lastScannedCode.value = null
  lastScanType.value = null
  
  // Resume scanning if it was active
  if (!isScanning.value) {
    startScanning()
  }
}

// Quantity controls
const increaseQuantity = () => {
  const maxStock = currentProduct.value?.stockQuantity || 999
  if (quantity.value < maxStock) {
    quantity.value++
  }
}

const decreaseQuantity = () => {
  if (quantity.value > 1) {
    quantity.value--
  }
}

// Sale processing
const processSale = async () => {
  if (!currentProduct.value) return
  
  processingSale.value = true
  
  try {
    // Use the new sales API that supports SKU/barcode identifiers
    const saleData = {
      identifier: currentProduct.value.sku || currentProduct.value.barcode,
      identifierType: currentProduct.value.sku ? 'sku' : 'barcode',
      quantitySold: quantity.value
    }
    
    console.log('💰 Processing sale:', saleData)
    
    const response = await api.post('/sales', saleData)
    
    console.log('✅ Sale processed:', response.data)
    
    // Add to recent sales
    recentSales.value.unshift({
      id: response.data.sale.id,
      productName: currentProduct.value.name,
      sku: currentProduct.value.sku,
      barcode: currentProduct.value.barcode,
      quantitySold: quantity.value,
      priceAtSale: currentProduct.value.price,
      createdAt: new Date()
    })
    
    // Keep only last 10 sales
    if (recentSales.value.length > 10) {
      recentSales.value = recentSales.value.slice(0, 10)
    }
    
    showNotification(`Sale processed successfully! Total: $${saleTotal.value.toFixed(2)}`, 'success')
    
    // Clear current product and resume scanning
    clearProduct()
    
  } catch (err) {
    console.error('Sale processing error:', err)
    showNotification(
      err.response?.data?.error || 'Failed to process sale',
      'error'
    )
  } finally {
    processingSale.value = false
  }
}

// Notification methods
const showNotification = (message, type = 'info') => {
  notification.value = { message, type }
  
  // Auto-clear after 5 seconds
  setTimeout(() => {
    notification.value = null
  }, 5000)
}

const clearNotification = () => {
  notification.value = null
}

// Utility methods
const formatTime = (date) => {
  return new Date(date).toLocaleTimeString()
}

// Load recent sales on mount
onMounted(async () => {
  try {
    const response = await api.get('/sales?limit=10')
    recentSales.value = response.data.sales || []
  } catch (err) {
    console.error('Failed to load recent sales:', err)
  }
})

// Cleanup on unmount
onUnmounted(() => {
  stopScanning()
})
</script>

<style scoped>
.sale-scanner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.scanner-header {
  text-align: center;
  margin-bottom: 30px;
}

.scanner-header h2 {
  color: #2c3e50;
  margin-bottom: 10px;
}

.scanner-header p {
  color: #7f8c8d;
}

.scanner-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 30px;
}

/* Search Section */
.search-section {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  min-height: 400px;
}

.search-header {
  margin-bottom: 20px;
}

.search-header h3 {
  color: #2c3e50;
  margin: 0 0 5px 0;
}

.search-header p {
  color: #6c757d;
  margin: 0;
  font-size: 0.9em;
}

/* Camera Section */
.camera-section {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  min-height: 400px;
}

.camera-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.camera-header h3 {
  color: #2c3e50;
  margin: 0;
}

.scanner-status {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.9em;
  font-weight: 500;
  background: #e9ecef;
  color: #6c757d;
}

.scanner-status.active {
  background: #28a745;
  color: white;
}

.camera-controls {
  text-align: center;
  margin-bottom: 20px;
}

.camera-info {
  margin-top: 10px;
  color: #6c757d;
  font-size: 0.9em;
}

.camera-viewport {
  position: relative;
  height: 300px;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
  margin-bottom: 20px;
}

.qr-reader {
  width: 100%;
  height: 100%;
}

.scanner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  display: flex;
  justify-content: center;
  align-items: center;
}

.scan-area {
  width: 200px;
  height: 200px;
  border: 2px solid #28a745;
  border-radius: 8px;
  position: relative;
  box-shadow: 0 0 0 1000px rgba(0, 0, 0, 0.3);
}

.scan-line {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: #28a745;
  animation: scan 2s linear infinite;
}

@keyframes scan {
  0% { top: 0; }
  100% { top: 100%; }
}

.error-message {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 8px;
  padding: 15px;
  text-align: center;
  color: #721c24;
}

/* Product Section */
.product-section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
}

.product-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 2px solid #f8f9fa;
  padding-bottom: 15px;
}

.product-header h3 {
  color: #2c3e50;
  margin: 0;
}

.last-scan {
  font-size: 0.9em;
  color: #6c757d;
  background: #f8f9fa;
  padding: 8px 12px;
  border-radius: 6px;
}

.loading {
  text-align: center;
  padding: 40px;
  color: #6c757d;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007bff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.product-info {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.product-details h4 {
  color: #2c3e50;
  margin: 0 0 10px 0;
  font-size: 1.3em;
}

.product-description {
  color: #6c757d;
  margin: 0 0 15px 0;
  font-style: italic;
}

.product-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 15px;
}

.product-meta span {
  background: #f8f9fa;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.9em;
  font-weight: 500;
}

.sku {
  background: #e3f2fd;
  color: #1976d2;
}

.barcode {
  background: #f3e5f5;
  color: #7b1fa2;
}

.global-product {
  background: #e8f5e8;
  color: #2e7d32;
}

.product-price {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
}

.price {
  font-size: 1.5em;
  font-weight: bold;
  color: #28a745;
}

.stock {
  color: #6c757d;
  font-weight: 500;
}

.quantity-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.quantity-controls label {
  font-weight: 500;
  color: #2c3e50;
}

.quantity-input {
  display: flex;
  align-items: center;
  border: 2px solid #e9ecef;
  border-radius: 6px;
  overflow: hidden;
}

.quantity-field {
  border: none;
  padding: 8px 12px;
  text-align: center;
  width: 80px;
  font-size: 1em;
}

.quantity-field:focus {
  outline: none;
}

.sale-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #007bff;
  color: white;
  padding: 15px;
  border-radius: 8px;
  font-size: 1.2em;
  font-weight: bold;
}

.sale-actions {
  display: flex;
  gap: 10px;
}

.sale-actions button {
  flex: 1;
}

.no-product {
  text-align: center;
  padding: 40px;
  color: #6c757d;
}

.placeholder-icon {
  font-size: 4em;
  margin-bottom: 20px;
}

.quick-actions {
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 20px;
}

/* Recent Sales */
.recent-sales {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.recent-sales h3 {
  color: #2c3e50;
  margin: 0 0 20px 0;
  border-bottom: 2px solid #f8f9fa;
  padding-bottom: 10px;
}

.sales-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.sale-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.sale-item:hover {
  background: #e9ecef;
  transform: translateY(-2px);
}

.sale-info {
  flex: 1;
}

.product-name {
  font-weight: bold;
  color: #2c3e50;
  display: block;
  margin-bottom: 5px;
}

.sale-details {
  color: #6c757d;
  font-size: 0.9em;
  display: block;
  margin-bottom: 3px;
}

.sale-identifier {
  color: #007bff;
  font-size: 0.8em;
  font-weight: 500;
}

.sale-total {
  font-weight: bold;
  color: #28a745;
  font-size: 1.1em;
  margin: 0 20px;
}

.sale-time {
  color: #6c757d;
  font-size: 0.9em;
  min-width: 80px;
  text-align: right;
}

/* Notification */
.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 15px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 1000;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.notification.success {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
}

.notification.error {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

.notification.info {
  background: #d1ecf1;
  border: 1px solid #bee5eb;
  color: #0c5460;
}

.notification-close {
  background: none;
  border: none;
  font-size: 1.2em;
  cursor: pointer;
  color: inherit;
  padding: 0;
  margin-left: 10px;
}

/* Buttons */
.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1em;
  font-weight: 500;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.btn:active {
  transform: translateY(0);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover {
  background: #0056b3;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #545b62;
}

.btn-success {
  background: #28a745;
  color: white;
}

.btn-success:hover {
  background: #1e7e34;
}

.btn-outline {
  background: transparent;
  color: #007bff;
  border: 2px solid #007bff;
}

.btn-outline:hover {
  background: #007bff;
  color: white;
}

.btn-large {
  padding: 15px 30px;
  font-size: 1.1em;
}

.btn-small {
  padding: 5px 10px;
  font-size: 0.9em;
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid #ffffff;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Responsive Design */
@media (max-width: 768px) {
  .scanner-container {
    grid-template-columns: 1fr;
  }
  
  .product-price {
    flex-direction: column;
    gap: 10px;
    text-align: center;
  }
  
  .sale-actions {
    flex-direction: column;
  }
  
  .sale-item {
    flex-direction: column;
    gap: 10px;
    text-align: center;
  }
  
  .quick-actions {
    flex-direction: column;
    align-items: center;
  }
  
  .notification {
    position: fixed;
    left: 20px;
    right: 20px;
    top: 20px;
  }
}
</style> 